import {
  IStyles,
  getImageWithFallback,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { CloudUpload } from '@protos/learning_management/lms.db_pb';
import { useEffect, useMemo, useState } from 'react';

const styles: IStyles = {
  modal: {
    '& .MuiDialog-paper': {
      width: { xs: '92vw', md: '80vw', lg: pxTovW(860) },
      m: { xs: '4vw', md: pxToRem(32) },
      maxWidth: 'none',
      borderRadius: { xs: pxToRem(20), md: pxToRem(18), lg: pxTovW(20) },
      p: { xs: pxToRem(16), md: pxToRem(21), lg: pxTovW(24) },
      boxSizing: 'border-box',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: pxToRem(10),
  },
  mediaBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F4F7FB',
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    minHeight: { xs: pxToRem(180), md: pxToRem(220), lg: pxTovW(260) },
    p: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
  },
  media: {
    maxWidth: '100%',
    maxHeight: '60vh',
    objectFit: 'contain',
    borderRadius: { xs: pxToRem(8), md: pxToRem(8), lg: pxTovW(8) },
  },
};

interface IProps {
  open: boolean;
  /** Named in the heading so the teacher knows whose evidence they opened. */
  groupName?: string;
  /** The group's attached files; anything that is not a photo or video is ignored. */
  uploads: CloudUpload[];
  handleClose: () => void;
}

/** The bucket order the rest of the teacher flow reads student uploads through. */
const resolveUploadUrl = (upload?: CloudUpload) =>
  getImageWithFallback(
    upload?.gcpFileUrl || upload?.previewUrl || upload?.signedUrl || '',
    'userDataBucket',
    'schoolnetCdnImagesBucket'
  );

/**
 * Shows the photo and the short video a student attached when submitting a
 * project.
 *
 * Both are optional for the student, so a tab is rendered only for what was
 * actually attached - never an empty "Video" tab standing in for a group that
 * only sent a photo.
 */
export const ProjectEvidenceDialog = (props: IProps) => {
  const { open, groupName, uploads, handleClose } = props;

  const photo = useMemo(
    () => uploads.find((upload) => upload.fileType === FileEnum.FILE_TYPE_IMAGE),
    [uploads]
  );
  const video = useMemo(
    () => uploads.find((upload) => upload.fileType === FileEnum.FILE_TYPE_VIDEO),
    [uploads]
  );

  const tabs = useMemo(
    () =>
      [
        photo ? ('photo' as const) : undefined,
        video ? ('video' as const) : undefined,
      ].filter((tab): tab is 'photo' | 'video' => tab !== undefined),
    [photo, video]
  );

  const [activeTab, setActiveTab] = useState<'photo' | 'video'>('photo');
  const [urls, setUrls] = useState<{ photo?: string; video?: string }>({});

  useEffect(() => {
    //~ The first tab that exists - a video-only group must not open on a
    //~ 'photo' tab that was never rendered.
    if (tabs.length > 0) {
      setActiveTab(tabs[0]);
    }
  }, [open, tabs]);

  useEffect(() => {
    let isMounted = true;
    //~ Resolved only while the dialog is open: each lookup is a HEAD request
    //~ per file, and every group on the page holds evidence of its own.
    if (!open) {
      return;
    }
    (async () => {
      const [photoUrl, videoUrl] = await Promise.all([
        resolveUploadUrl(photo),
        resolveUploadUrl(video),
      ]);
      if (isMounted) {
        setUrls({ photo: photoUrl, video: videoUrl });
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [open, photo, video]);

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.modal}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: pxToRem(14), md: pxToRem(16), lg: pxTovW(16) },
        }}
      >
        <Box sx={styles.header}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h2" fontWeight="bold">
              Project Evidence
            </Typography>
            {groupName && (
              <Typography variant="subText" color="#9F9F9F">
                {groupName}
              </Typography>
            )}
          </Box>
          <IconButton aria-label="Close" size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {tabs.length === 0 ? (
          <Typography variant="elementBodyText">
            No evidence submitted by the student
          </Typography>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              textColor="primary"
              indicatorColor="primary"
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab}
                  value={tab}
                  label={tab === 'photo' ? 'Photo' : 'Video'}
                />
              ))}
            </Tabs>

            <Box sx={styles.mediaBox}>
              {activeTab === 'photo' ? (
                <Box
                  component="img"
                  src={urls.photo}
                  alt={photo?.fileName || 'Project photo'}
                  sx={styles.media}
                />
              ) : (
                <Box
                  component="video"
                  src={urls.video}
                  controls
                  sx={{ ...styles.media, width: '100%' }}
                />
              )}
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default ProjectEvidenceDialog;
